import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("%cUncaught error in React component tree:", 'color: red; font-weight: bold;', error, errorInfo);

    // Special handling for date-related errors
    if (error.message.includes('date value is not finite') ||
        error.message.includes('DateTimeFormat') ||
        error.message.includes('toLocaleTimeString') ||
        error.message.includes('Invalid Date')) {
      console.error('%cDATE ERROR CAUGHT:', 'color: orange; font-weight: bold;',
        'This error was prevented from crashing the dashboard. The date validation system should prevent this.');

      // Clear any potentially corrupted cached data
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          // Clear any cached sports data that might contain invalid dates
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.includes('sports') || key.includes('game') || key.includes('cache')) {
              localStorage.removeItem(key);
            }
          });
        } catch (e) {
          console.warn('Could not clear localStorage:', e);
        }
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4 bg-background">
            <div className="w-full max-w-lg p-8 text-center bg-surface rounded-xl shadow-lg border border-red-500/20">
                <div className="text-5xl mb-4">🚨</div>
                <h1 className="text-2xl font-bold text-red-400 mb-2">Something went wrong.</h1>
                <p className="text-text-secondary mb-6">
                    {this.state.error?.message.includes('date') ?
                      'A date formatting error occurred. This has been automatically resolved. Please refresh the page.' :
                      'We\'ve encountered an unexpected error. Please try refreshing the page. If the problem persists, please check the console for more details.'
                    }
                </p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-primary text-white font-bold py-2 px-6 rounded-lg hover:bg-opacity-90 transition-colors"
                >
                    Refresh Page
                </button>
                {this.state.error && (
                    <details className="mt-6 text-left text-xs bg-background p-3 rounded-lg">
                        <summary className="cursor-pointer font-semibold text-text-secondary">Error Details</summary>
                        <pre className="mt-2 whitespace-pre-wrap break-all text-red-400/80">
                            {this.state.error.stack || this.state.error.toString()}
                        </pre>
                    </details>
                )}
            </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;